import MarketingLayout from '../components/marketing/MarketingLayout';

export default function TermsPage() {
  return (
    <MarketingLayout>
      <section className="section-tight">
        <div className="container">
          <div className="doc">
        <h1 className="text-4xl font-bold text-navy-900 mb-4">Terms of Service</h1>
        <p className="text-gray-600 mb-2">End User License Agreement (EULA)</p>
        <p className="text-gray-600 mb-8">Effective date: February 18, 2026</p>

        <div className="prose prose-lg max-w-none">

          {/* ── 1. Acceptance of Terms ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing, browsing, or using the Clarid platform (the &ldquo;Service&rdquo;), including
              the web dashboard, API endpoints, and MCP server integration, you
              (&ldquo;Customer,&rdquo; &ldquo;you,&rdquo; or &ldquo;your&rdquo;) acknowledge that you have
              read, understood, and agree to be bound by these Terms of Service and End User License
              Agreement (collectively, the &ldquo;Agreement&rdquo;). If you do not agree to all of the
              terms and conditions of this Agreement, you must not access or use the Service.
            </p>
            <p className="text-gray-700 mb-4">
              If you are accessing or using the Service on behalf of a financial institution, bank,
              credit union, or any other organization (collectively, &ldquo;Institution&rdquo;), you
              represent and warrant that you have the authority to bind that Institution to this
              Agreement. In such cases, &ldquo;you&rdquo; and &ldquo;your&rdquo; refer to that
              Institution. If you do not have such authority, or if the Institution does not agree with
              these terms, you must not access or use the Service.
            </p>
            <p className="text-gray-700 mb-4">
              This Agreement constitutes a legally binding contract between you and Clarid, Inc.
              (&ldquo;Clarid,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), a
              corporation organized under the laws of the State of Kansas, United States of America.
            </p>
          </div>

          {/* ── 2. Description of Service ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              Clarid is a compliance intelligence platform designed for financial institutions,
              including banks, credit unions, and other regulated entities. The Service provides
              automated compliance checking and analysis tools to help institutions evaluate marketing
              materials, advertising content, and public-facing communications against applicable
              federal and state regulations, including but not limited to Regulation Z (Truth in
              Lending), Regulation DD (Truth in Savings), Regulation E (Electronic Fund Transfers),
              UDAAP (Unfair, Deceptive, or Abusive Acts or Practices), and the Equal Credit
              Opportunity Act.
            </p>
            <p className="text-gray-700 mb-4">
              The Service is available through the following channels:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Web Dashboard</strong> &mdash; A browser-based interface for uploading,
                reviewing, and managing compliance checks, audit trails, and institutional settings.
              </li>
              <li>
                <strong>MCP Server Integration</strong> &mdash; A Model Context Protocol (MCP) server
                that integrates with Microsoft 365 Copilot, enabling compliance checking directly
                within the tools and workflows your team already uses. When the MCP server is
                connected, Copilot can invoke Clarid&rsquo;s compliance engine in real time.
              </li>
              <li>
                <strong>API Access</strong> &mdash; RESTful API endpoints for programmatic integration
                with your existing content management, marketing automation, or document review
                systems.
              </li>
            </ul>
            <p className="text-gray-700 mb-4 font-semibold">
              Important: The Service provides compliance checking assistance and is intended as a
              supplementary tool only. Clarid does not provide legal advice. The Service does not
              replace and is not a substitute for human compliance review, qualified compliance
              officers, or licensed legal counsel. All compliance determinations, flags, and
              recommendations generated by the Service should be reviewed and validated by qualified
              compliance personnel before any action is taken.
            </p>
          </div>

          {/* ── 3. Account Terms ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">3. Account Terms</h2>
            <p className="text-gray-700 mb-4">
              To use the Service, you must create an account and provide accurate, complete, and
              current information during the registration process. You agree to update your account
              information promptly to keep it accurate and complete at all times.
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Account Security.</strong> You are responsible for maintaining the
                confidentiality of your account credentials, including your password and any
                authentication tokens. You agree to notify Clarid immediately of any unauthorized
                access to or use of your account. Clarid shall not be liable for any loss or damage
                arising from your failure to safeguard your account credentials.
              </li>
              <li>
                <strong>API Key Confidentiality.</strong> API keys issued to your account are
                confidential and must be treated as sensitive credentials. You must not share, publish,
                or expose API keys in client-side code, public repositories, or any other insecure
                location. You are responsible for all activity that occurs using your API keys.
              </li>
              <li>
                <strong>One Institution Per Account.</strong> Each account is associated with a single
                financial institution. If you operate or manage multiple institutions, each institution
                requires its own separate account and subscription.
              </li>
              <li>
                <strong>Multi-Tenant Isolation.</strong> The Service operates in a multi-tenant
                architecture. Clarid maintains strict logical isolation between tenant data. Your
                institution&rsquo;s data, compliance checks, audit logs, and configuration settings
                are isolated from those of other institutions. You must not attempt to access, view,
                or modify data belonging to any other tenant.
              </li>
              <li>
                <strong>Authorized Users.</strong> You may invite additional users within your
                Institution to access the Service under your account. You are responsible for ensuring
                that all authorized users comply with this Agreement and for all actions taken by
                authorized users under your account.
              </li>
            </ul>
          </div>

          {/* ── 4. Subscription and Billing ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">4. Subscription and Billing</h2>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">4.1 Subscription Plans</h3>
            <p className="text-gray-700 mb-4">
              The Service is offered under the following subscription plans:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Professional Plan:</strong> $999 per month, billed monthly, or $9,990 per
                year, billed annually (representing a discount for annual commitment). The
                Professional Plan includes access to the web dashboard, MCP server integration, API
                access, compliance checking for all supported regulation types, audit trail
                functionality, and standard support.
              </li>
              <li>
                <strong>Enterprise Plan:</strong> Custom pricing based on institutional needs,
                volume, and required integrations. Enterprise Plans may include dedicated support,
                custom rule configuration, advanced reporting, priority processing, and other
                features as agreed upon in a separate Enterprise Agreement or Order Form.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">4.2 Founding Partner Pricing</h3>
            <p className="text-gray-700 mb-4">
              Clarid may offer preferential &ldquo;Founding Partner&rdquo; pricing to qualifying early
              customers who adopt the Service during its initial launch period. Founding Partner
              pricing terms, including any discounts, locked rates, or additional benefits, will be
              set forth in a separate written agreement or addendum. Founding Partner pricing is
              non-transferable and subject to the terms of the applicable Founding Partner agreement.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">4.3 Payment Processing</h3>
            <p className="text-gray-700 mb-4">
              All payments are processed securely through Stripe, Inc. (&ldquo;Stripe&rdquo;). By
              subscribing to the Service, you agree to Stripe&rsquo;s terms of service and privacy
              policy as they relate to payment processing. Clarid does not store your full credit card
              number, bank account details, or other sensitive payment information on its servers.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">4.4 Billing and Refund Policy</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Annual Subscriptions:</strong> Annual subscription fees are billed in advance
                for the full year. Annual subscriptions are non-refundable after thirty (30) days from
                the date of purchase or renewal. Within the first thirty (30) days, you may request a
                full refund by contacting support@clarid.ai. After the thirty (30) day period, no
                partial or prorated refunds will be issued for the remainder of the annual term.
              </li>
              <li>
                <strong>Monthly Subscriptions:</strong> Monthly subscription fees are billed in
                advance at the beginning of each billing cycle. You may cancel a monthly subscription
                at any time. Upon cancellation, you will retain access to the Service through the end
                of your current paid billing period. No refunds or credits will be issued for partial
                months.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">4.5 Price Changes</h3>
            <p className="text-gray-700 mb-4">
              Clarid reserves the right to modify subscription pricing. We will provide at least sixty
              (60) days&rsquo; advance written notice of any price changes via email to the address
              associated with your account. Price changes will take effect at the beginning of your
              next billing cycle following the sixty (60) day notice period. If you do not agree to
              the revised pricing, you may cancel your subscription before the new pricing takes
              effect.
            </p>
          </div>

          {/* ── 5. Acceptable Use ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">5. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">
              You agree to use the Service only for its intended purpose: legitimate compliance
              checking and review of marketing materials, advertising content, and public-facing
              communications produced by or on behalf of your Institution. You agree not to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                Submit content containing personally identifiable information (PII) of customers,
                including but not limited to Social Security numbers, account numbers, dates of birth,
                or other sensitive personal data. The Service is designed to analyze marketing
                materials and advertising copy, not individual customer records.
              </li>
              <li>
                Attempt to access, view, modify, or exfiltrate data belonging to any other customer,
                institution, or tenant of the Service, whether through the web dashboard, API, MCP
                server, or any other means.
              </li>
              <li>
                Reverse-engineer, decompile, disassemble, or otherwise attempt to derive the source
                code, algorithms, data models, or underlying architecture of the Service, including
                the compliance rules engine, scoring algorithms, or regulation mappings.
              </li>
              <li>
                Use the Service for any purpose that violates applicable federal, state, or local
                laws, regulations, or ordinances, or that facilitates illegal activity of any kind.
              </li>
              <li>
                Circumvent, disable, or interfere with rate limits, usage quotas, API throttling, or
                other technical measures designed to ensure fair use and Service stability. This
                includes but is not limited to creating multiple accounts to circumvent per-account
                limits, scripting automated requests in excess of documented rate limits, or using
                any means to bypass authentication or authorization controls.
              </li>
              <li>
                Redistribute, resell, sublicense, or otherwise make the Service available to any
                third party without the prior written consent of Clarid.
              </li>
              <li>
                Introduce viruses, malware, trojans, or other malicious code into the Service, or
                engage in any activity that disrupts, degrades, or impairs the performance or
                integrity of the Service or its underlying infrastructure.
              </li>
            </ul>
            <p className="text-gray-700 mb-4">
              Clarid reserves the right to investigate and take appropriate action against any
              violation of this section, including but not limited to suspending or terminating your
              access to the Service, removing offending content, and reporting violations to law
              enforcement authorities.
            </p>
          </div>

          {/* ── 6. Intellectual Property ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">6. Intellectual Property</h2>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">6.1 Clarid&rsquo;s Intellectual Property</h3>
            <p className="text-gray-700 mb-4">
              The Service, including but not limited to the compliance rules engine, scoring
              algorithms, regulation mappings, user interface designs, software code, documentation,
              APIs, MCP server implementation, and all associated intellectual property, is and shall
              remain the exclusive property of Clarid, Inc. The Service is protected by copyright,
              trade secret, patent, and other intellectual property laws of the United States and
              international treaties. Nothing in this Agreement grants you any right, title, or
              interest in the Service beyond the limited license to use it in accordance with these
              terms.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">6.2 Customer Content</h3>
            <p className="text-gray-700 mb-4">
              You retain all right, title, and ownership interest in the content you submit to the
              Service for compliance checking (&ldquo;Customer Content&rdquo;), including marketing
              materials, advertising copy, images, and other media. By submitting Customer Content to
              the Service, you grant Clarid a limited, non-exclusive, non-transferable license to
              process, analyze, and store that content solely for the purpose of providing the Service
              to you, generating compliance reports, and maintaining audit trail records.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">6.3 Aggregated and Anonymized Data</h3>
            <p className="text-gray-700 mb-4">
              Clarid may collect and use aggregated, anonymized, and de-identified data derived from
              your use of the Service for the purposes of improving and enhancing the Service,
              developing new features, conducting research, generating industry benchmarks, and
              improving compliance detection accuracy. Such aggregated data will not identify you, your
              Institution, or any individual. Clarid retains all rights to such aggregated and
              anonymized data.
            </p>
          </div>

          {/* ── 7. Compliance Disclaimer ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">7. Compliance Disclaimer</h2>
            <div className="bg-amber-50 border-l-4 border-amber-400 p-6 mb-4 rounded-r-lg">
              <p className="text-gray-800 font-semibold mb-3">
                PLEASE READ THIS SECTION CAREFULLY. IT CONTAINS CRITICAL LIMITATIONS ON THE NATURE
                AND SCOPE OF THE SERVICE.
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>
                  <strong>Not a Guarantee of Compliance.</strong> The Service is a compliance
                  assistance tool and does not guarantee that any content analyzed will be fully
                  compliant with all applicable laws, regulations, rules, or guidelines. Compliance
                  results, scores, and flags generated by the Service are informational in nature and
                  are provided as recommendations, not certifications.
                </li>
                <li>
                  <strong>Not Legal Advice.</strong> Nothing provided by the Service, including
                  compliance reports, flags, recommendations, scores, or any other output, constitutes
                  legal advice. The Service is not a law firm, does not practice law, and is not a
                  substitute for the advice of a licensed attorney. You should consult with qualified
                  legal counsel regarding specific compliance questions and obligations.
                </li>
                <li>
                  <strong>Not a Substitute for Legal Counsel.</strong> The Service is designed to
                  supplement, not replace, the professional judgment of qualified compliance officers,
                  compliance committees, and legal counsel. The use of the Service does not create an
                  attorney-client relationship between you and Clarid.
                </li>
                <li>
                  <strong>No Tool Guarantees Detection of All Violations.</strong> No automated
                  compliance tool, including the Service, can guarantee the detection of every
                  potential regulatory violation. Regulations are subject to interpretation, change
                  over time, and may vary by jurisdiction. The Service is continuously updated, but
                  there may be a delay between regulatory changes and corresponding updates to the
                  Service.
                </li>
                <li>
                  <strong>Institutions Remain Solely Responsible.</strong> Your Institution bears sole
                  and ultimate responsibility for the compliance of its marketing materials,
                  advertising content, and public-facing communications with all applicable laws and
                  regulations. The use of the Service does not transfer, diminish, or otherwise affect
                  your Institution&rsquo;s compliance obligations.
                </li>
                <li>
                  <strong>Human Review Required.</strong> All compliance flags, warnings, and
                  recommendations generated by the Service should be reviewed, validated, and acted
                  upon by qualified compliance personnel within your Institution. Automated results
                  should not be relied upon as the sole basis for compliance decisions.
                </li>
                <li>
                  <strong>No Liability for Regulatory Penalties.</strong> Clarid shall not be liable
                  for any fines, penalties, sanctions, consent orders, cease-and-desist orders,
                  enforcement actions, or other regulatory consequences imposed on your Institution by
                  any federal, state, or local regulatory authority, regardless of whether the content
                  at issue was analyzed using the Service.
                </li>
              </ul>
            </div>
          </div>

          {/* ── 8. Audit Trail and Data ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">8. Audit Trail and Data</h2>
            <p className="text-gray-700 mb-4">
              The Service maintains immutable audit trail records for all compliance checks performed
              under your account. Audit records include timestamps, the content submitted, compliance
              results, flags identified, the rules applied, and the identity of the user who initiated
              the check. Audit records are designed to be tamper-evident and cannot be modified or
              deleted by users.
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Regulatory Examination Support.</strong> Audit trail records are maintained in
                a format suitable for presentation during regulatory examinations conducted by
                federal or state banking regulators, including but not limited to the OCC, FDIC,
                Federal Reserve, NCUA, and state banking departments. You may produce audit records
                to examiners as evidence of your Institution&rsquo;s compliance review processes.
              </li>
              <li>
                <strong>Data Export.</strong> You may export your audit trail records, compliance
                reports, and associated data at any time during the term of your subscription through
                the web dashboard or API. Exported data is available in industry-standard formats,
                including XLSX and PDF.
              </li>
              <li>
                <strong>Post-Termination Data Access.</strong> Upon termination or expiration of your
                subscription, you will have thirty (30) days to export all of your data, including
                audit trail records, compliance reports, and submitted content. Clarid will make
                reasonable efforts to assist with data export during this period upon request.
              </li>
            </ul>
          </div>

          {/* ── 9. Service Level ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">9. Service Level</h2>
            <p className="text-gray-700 mb-4">
              Clarid targets a Service availability of 99.5% uptime, measured on a monthly basis,
              excluding scheduled maintenance windows and circumstances beyond Clarid&rsquo;s
              reasonable control. Uptime is calculated as the percentage of total minutes in a calendar
              month during which the Service is operational and accessible.
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Scheduled Maintenance.</strong> Clarid will provide at least forty-eight (48)
                hours&rsquo; advance notice of scheduled maintenance windows via email notification
                and/or in-app notification. Scheduled maintenance will be performed during off-peak
                hours whenever reasonably practicable.
              </li>
              <li>
                <strong>Third-Party Dependencies.</strong> Clarid is not responsible or liable for
                downtime, degradation, or unavailability of third-party services, including but not
                limited to Microsoft 365, Microsoft Copilot, Stripe, cloud hosting infrastructure
                providers, or any other third-party integrations. The availability of the MCP server
                integration is dependent on the availability of Microsoft 365 and Copilot services.
              </li>
              <li>
                <strong>MCP Server Graceful Degradation.</strong> If the Clarid MCP server becomes
                temporarily unavailable, Microsoft 365 Copilot will continue to operate normally
                without compliance checking functionality. Copilot will not be impaired, disrupted, or
                otherwise affected by the unavailability of the Clarid MCP server. Compliance checks
                can be performed through the web dashboard or API during any period of MCP server
                unavailability.
              </li>
            </ul>
            <p className="text-gray-700 mb-4">
              Enterprise customers may negotiate enhanced service level agreements (SLAs) with
              specific uptime commitments, response times, and remedies as part of a separate
              Enterprise Agreement.
            </p>
          </div>

          {/* ── 10. Limitation of Liability ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, CLARID&rsquo;S TOTAL AGGREGATE
              LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THIS AGREEMENT OR YOUR USE
              OF THE SERVICE SHALL NOT EXCEED THE TOTAL AMOUNT OF FEES ACTUALLY PAID BY YOU TO CLARID
              DURING THE TWELVE (12) MONTH PERIOD IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE
              CLAIM.
            </p>
            <p className="text-gray-700 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL CLARID, ITS
              OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, OR LICENSORS BE LIABLE FOR ANY OF
              THE FOLLOWING, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT
              LIABILITY, OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT CLARID HAS BEEN ADVISED OF THE
              POSSIBILITY OF SUCH DAMAGES:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Any indirect, incidental, special, consequential, or punitive damages;</li>
              <li>Loss of profits, revenue, business opportunities, or goodwill;</li>
              <li>Loss of data or costs of data recovery;</li>
              <li>Costs of procurement of substitute goods or services;</li>
              <li>
                Any regulatory fines, penalties, sanctions, enforcement actions, consent orders, or
                other regulatory consequences imposed by any governmental or regulatory authority;
              </li>
              <li>
                Any damages arising from your reliance on the Service&rsquo;s compliance results,
                flags, recommendations, or other outputs;
              </li>
              <li>
                Any damages arising from unauthorized access to or alteration of your data or
                transmissions.
              </li>
            </ul>
            <p className="text-gray-700 mb-4">
              The limitations in this section shall apply even if any limited remedy provided herein is
              found to have failed of its essential purpose. Some jurisdictions do not allow the
              exclusion or limitation of certain damages, so some of the above limitations may not
              apply to you to the extent prohibited by applicable law.
            </p>
          </div>

          {/* ── 11. Indemnification ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">11. Indemnification</h2>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">11.1 Your Indemnification of Clarid</h3>
            <p className="text-gray-700 mb-4">
              You agree to indemnify, defend, and hold harmless Clarid, its officers, directors,
              employees, agents, and affiliates from and against any and all claims, demands, losses,
              liabilities, damages, costs, and expenses (including reasonable attorneys&rsquo; fees)
              arising out of or relating to: (a) your use of the Service; (b) your violation of this
              Agreement; (c) your violation of any applicable law, regulation, or rule; (d) any
              Customer Content you submit to the Service; or (e) any claim by a third party resulting
              from your Institution&rsquo;s marketing materials, advertising content, or
              public-facing communications, regardless of whether such content was analyzed using the
              Service.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">11.2 Clarid&rsquo;s Indemnification of You</h3>
            <p className="text-gray-700 mb-4">
              Clarid agrees to indemnify, defend, and hold harmless you and your Institution from and
              against any and all claims, demands, losses, liabilities, damages, costs, and expenses
              (including reasonable attorneys&rsquo; fees) arising out of or relating to: (a) a claim
              that the Service infringes upon or misappropriates any valid United States patent,
              copyright, or trade secret of a third party; or (b) a breach by Clarid of its
              obligations under this Agreement, including any unauthorized disclosure of your data
              caused by Clarid&rsquo;s negligence or willful misconduct.
            </p>
            <p className="text-gray-700 mb-4">
              Clarid&rsquo;s indemnification obligation is contingent upon you providing Clarid with:
              (i) prompt written notice of the claim; (ii) sole control of the defense and settlement
              of the claim; and (iii) reasonable cooperation and assistance in the defense of the
              claim at Clarid&rsquo;s expense.
            </p>
          </div>

          {/* ── 12. Termination ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">12. Termination</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Termination by Either Party.</strong> Either party may terminate this Agreement
                by providing thirty (30) days&rsquo; written notice to the other party via email.
                Written notice of termination must be sent to support@clarid.ai (if terminating by
                you) or to the email address associated with your account (if terminating by Clarid).
              </li>
              <li>
                <strong>Immediate Termination for Cause.</strong> Clarid reserves the right to
                suspend or terminate your access to the Service immediately and without prior notice
                if you violate Section 5 (Acceptable Use) of this Agreement, engage in fraudulent
                activity, or if continued access poses a security risk to the Service or other
                customers. Clarid will make reasonable efforts to notify you of the reason for
                termination promptly thereafter.
              </li>
              <li>
                <strong>Data Export Upon Termination.</strong> Upon termination or expiration of this
                Agreement for any reason, you will have thirty (30) days from the effective date of
                termination to export all of your data from the Service, including Customer Content,
                audit trail records, compliance reports, and account information.
              </li>
              <li>
                <strong>Data Deletion.</strong> Following the thirty (30) day data export period,
                Clarid will delete your data from its active systems within sixty (60) days, unless
                retention is required by applicable law, regulation, or valid legal process (such as a
                litigation hold, subpoena, or regulatory examination). Any data retained pursuant to
                legal requirements will be stored securely and deleted promptly once the retention
                obligation expires.
              </li>
              <li>
                <strong>Survival.</strong> The following sections shall survive termination or
                expiration of this Agreement: Section 6 (Intellectual Property), Section 7 (Compliance
                Disclaimer), Section 10 (Limitation of Liability), Section 11 (Indemnification),
                Section 13 (Governing Law), and any accrued payment obligations.
              </li>
            </ul>
          </div>

          {/* ── 13. Governing Law ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">13. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              This Agreement shall be governed by and construed in accordance with the laws of the
              State of Kansas, United States of America, without regard to its conflict of laws
              principles.
            </p>
            <p className="text-gray-700 mb-4">
              Any dispute, claim, or controversy arising out of or relating to this Agreement or the
              breach, termination, enforcement, interpretation, or validity thereof, including the
              determination of the scope or applicability of this Agreement, shall be brought
              exclusively in the state or federal courts located in Riley County, Kansas. Each party
              irrevocably consents to the exclusive jurisdiction and venue of such courts and waives
              any objection to jurisdiction or venue, including any objection based on inconvenient
              forum.
            </p>
          </div>

          {/* ── 14. Changes to Terms ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">14. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              Clarid reserves the right to modify, amend, or update this Agreement at any time. For
              material changes to this Agreement, Clarid will provide at least thirty (30)
              days&rsquo; advance written notice via email to the address associated with your account
              and/or through a prominent notice within the Service. Material changes include, but are
              not limited to, changes to pricing, liability limitations, data handling practices, or
              the scope of the Service.
            </p>
            <p className="text-gray-700 mb-4">
              Your continued use of the Service after the effective date of any changes to this
              Agreement constitutes your acceptance of the revised terms. If you do not agree to the
              revised terms, you must discontinue your use of the Service and terminate your account
              before the changes take effect.
            </p>
            <p className="text-gray-700 mb-4">
              Non-material changes, such as typographical corrections, formatting updates, or
              clarifications that do not substantively alter the rights or obligations of either party,
              may be made without advance notice. The &ldquo;Effective date&rdquo; at the top of this
              Agreement will be updated to reflect the date of the most recent revision.
            </p>
          </div>

          {/* ── 15. Contact ── */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">15. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions, concerns, or requests regarding this Agreement or the
              Service, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 text-gray-700">
              <p className="font-semibold mb-2">Clarid, Inc.</p>
              <p className="mb-1">
                Email:{' '}
                <a
                  href="mailto:support@clarid.ai"
                  className="text-ocean-600 hover:text-ocean-700 underline"
                >
                  support@clarid.ai
                </a>
              </p>
            </div>
          </div>

          {/* ── Closing ── */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Clarid, Inc. All rights reserved. This document was
              last updated on February 18, 2026.
            </p>
          </div>

        </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
